import * as React from 'react';

import { IconCash } from '@tock/icons';
import { IconMoney } from 'ui/icons';
import { OfferingPriceDescriptionDisplay } from 'components/business/offering/OfferingPriceDescriptionDisplay';
import {
  PriceRange,
  getIsRequiredSupplementZero,
  getPriceDistribution,
  hasPriceRanges,
} from 'components/business/PriceRange';
import { consumer, core } from '@tock/coretypes';
import { getServesContent } from 'utils/experience';
import { useExperimentVariant } from 'hooks/useExperiment';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import { useIsSakeIconSetEnabled } from 'omakase/utils/IconMigrationUtil';
import OfferingDetailsMetadataItem from 'components/business/offering/OfferingDetailsMetadataItem';

interface Props {
  price: consumer.OfferedPriceSummary;
  type: consumer.Offering.Type;
  descriptiveVariety?: core.DescriptiveVariety;
  singleUnitQuantity?: number;
  isReschedule?: boolean;
}

export const OfferingDetailsPrice: React.FunctionComponent<Props> = ({
  descriptiveVariety,
  price,
  type,
  singleUnitQuantity,
  isReschedule = false,
}) => {
  const disableExperiment = useFeatureFlag('CONSUMER_DISABLE_DEPOSIT_EXPERIMENTS');
  const hidePriceExperiment =
    useExperimentVariant('DepositDemandExperiment') === 1 &&
    type === 'DEPOSIT' &&
    !disableExperiment;
  const isSakeIconSetEnabled = useIsSakeIconSetEnabled();

  const isMinRequiredSpendDisplay = price.priceDisplayStyle === 'MIN_REQUIRED_SPEND';
  const isItemizedDisplay = price.priceDisplayStyle === 'ITEMIZED';
  const isFreeReservationWithPaidRequiredAddOns =
    !getIsRequiredSupplementZero(price) &&
    (type === 'RESERVATION' ||
      ((price.pricePerPerson?.minCents ?? 0) === 0 &&
        (price?.pricePerPerson?.maxCents ?? 0) === 0));

  const shouldShowPerTicketPrice =
    hasPriceRanges(price) &&
    ((isFreeReservationWithPaidRequiredAddOns && isItemizedDisplay) || type !== 'RESERVATION');

  const hasNoPrice = isMinRequiredSpendDisplay
    ? (price.minRequiredSpend?.totalCents ?? 0) <= 0
    : !shouldShowPerTicketPrice;

  const isRescheduleWithMinRequiredSpend = isReschedule && isMinRequiredSpendDisplay;

  if (hidePriceExperiment || hasNoPrice || isRescheduleWithMinRequiredSpend) {
    return null;
  }

  function getPriceContent() {
    if (isItemizedDisplay && isFreeReservationWithPaidRequiredAddOns) {
      const paidSupplementsByType = new Map();

      // Group required paid supplements by type
      for (const supplement of price.supplements) {
        if (supplement.price?.minCents === 0) {
          continue;
        }
        paidSupplementsByType.set(supplement.type, [
          ...(paidSupplementsByType.get(supplement.type) || []),
          supplement,
        ]);
      }
      const hasPaidRequiredAddonsOfMultipleTypes = paidSupplementsByType.size > 1;

      // If we're rendering the price for type "RESERVATION" (this might include free reservations,
      // credit card hold, or large party deposit), only consider the supplement price.
      const effectivePrice: consumer.OfferedPriceSummary = isFreeReservationWithPaidRequiredAddOns
        ? { ...price, pricePerPerson: { minCents: 0, maxCents: 0 } }
        : price;

      return (
        <>
          {hasPaidRequiredAddonsOfMultipleTypes ? (
            'Required add-ons'
          ) : (
            <PriceRange
              price={effectivePrice}
              type={type}
              descriptiveVariety={descriptiveVariety}
            />
          )}
          {getServesContent(descriptiveVariety, singleUnitQuantity)}
        </>
      );
    } else if (isMinRequiredSpendDisplay) {
      return (
        <OfferingPriceDescriptionDisplay
          minRequiredSpendCents={price.minRequiredSpend?.totalCents ?? undefined}
          priceDescription={price.priceDescription}
          priceVariability={price.minRequiredSpend?.variability ?? undefined}
        />
      );
    } else {
      return (
        <>
          <PriceRange
            price={price}
            type={type}
            descriptiveVariety={descriptiveVariety}
            priceDistribution={getPriceDistribution(type, price)}
          />
          {getServesContent(descriptiveVariety, singleUnitQuantity)}
        </>
      );
    }
  }

  return (
    <OfferingDetailsMetadataItem icon={isSakeIconSetEnabled ? IconCash : IconMoney}>
      <div data-testid="offering-price">{getPriceContent()}</div>
    </OfferingDetailsMetadataItem>
  );
};
